import {acceptHMRUpdate, defineStore} from "pinia";
import {taskApi} from "@/container";
import LiveItemMapperService from "../core/services/LiveItemMapperService";
import {
    HomeInterviewInstanceItem,
    HomeOrderItem, HomePatientUserItem, HomeSellinItem, HomeSharingItem,
    HomeSurveyAnswerInstanceItem,
    OrderCreatedBroadcastEvent
} from "@/home/interfaces";
import {LiveItemType} from "@/core/interfaces/live/LiveItem";
import dayjs from "dayjs";

export type TaskItem = HomeInterviewInstanceItem | HomeOrderItem | HomeSurveyAnswerInstanceItem | HomeSharingItem | HomeSellinItem | HomePatientUserItem;

interface LiveStore {
    tasks: TaskItem[];
    loading: boolean;
    refreshing: boolean;
    loaded: boolean;
    hasMoreTasks: boolean;
}

export const useLiveStore = defineStore('live', {
    state: (): LiveStore => ({
        tasks: [],
        loading: false,
        loaded: false,
        refreshing: false,
        hasMoreTasks: false
    }),
    actions: {
        /**
         * Add a new order to the list of orders.
         *
         * @param item
         */
        newBroadcastOrder(item: OrderCreatedBroadcastEvent) {
            if (!this.tasks.find(
                previousItem => previousItem.type === LiveItemType.ORDER_CREATED && previousItem.data.id === item.id)
            ) {
                this.tasks.unshift(LiveItemMapperService().newOrder(-1, item));
            }
        },
        /**
         * Set a task as completed for a specific live item
         */
        async updateItem(id: number, type: LiveItemType) {
            const index = this.tasks.findIndex(
                (currentItem) => currentItem.type === type && currentItem.data.id === id
            );

            if (index > -1) {
                const task = this.tasks[index];
                return taskApi().update(task.id, dayjs().format('YYYY-MM-DD HH:mm:ss'))
                    .then(() => this.tasks.splice(index, 1));
            }
            return Promise.resolve();
        },
        /**
         * Delete task item.
         */
        deleteItems(item: any, type: LiveItemType) {
            const index = this.tasks.findIndex(
                (currentItem) => {
                    return currentItem.type === type && currentItem.data.id === item.id;
                }
            );

            if (index > -1) {
                this.tasks.splice(index, 1);
            }
        },
        /**
         * Load all the live items.
         */
        async loadItems() {
            this.loading = true;
            return taskApi().index()
                .then(response => {
                    this.tasks = response.data?.map(task => LiveItemMapperService().newTask(task))?.filter(item => !!item) ?? [];
                    this.hasMoreTasks = (response.meta?.total ?? 0) > this.tasks.length;
                })
                .finally(() => {
                    this.loaded = true;
                    this.loading = false;
                });
        },

        /**
         * Refresh the live items.
         */
        async refreshItems() {
            this.refreshing = true;
            return taskApi().index()
                .then(response => {
                    this.tasks = response.data?.map(task => LiveItemMapperService().newTask(task))?.filter(item => !!item) ?? [];
                    this.hasMoreTasks = (response.meta?.total ?? 0) > this.tasks.length;
                })
                .finally(() => {
                    this.refreshing = false;
                });
        }
    }
});

//https://pinia.vuejs.org/cookbook/hot-module-replacement.html#HMR-Hot-Module-Replacement-
if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useLiveStore, import.meta.hot));
}
